import { ref } from "vue";
import ProjectService, { PROJECT_CONSTANT } from "../services/projectService.js";

// Module-level singleton — shared between the Projects view and the side
// panel's "all projects" browsing mode, so opening either warms the other.
const projects = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(true);
let cursor = null;
let hasLoadedOnce = false;
let lastSortBy = "LAST_MODIFIED";
let lastSortDirection = "DESC";

const projectService = new ProjectService();

export function useProjectsList() {
  async function ensureLoaded() {
    if (hasLoadedOnce) return;
    hasLoadedOnce = true;
    isLoading.value = true;
    await loadMore();
    isLoading.value = false;
  }

  async function loadMore() {
    if (!hasMore.value || isLoadingMore.value) return;
    isLoadingMore.value = true;
    try {
      const res = await projectService.getProjects({
        projectType: PROJECT_CONSTANT.PROJECT_TYPES.ALL,
        projectsCursor: cursor,
        sortBy: lastSortBy,
        sortDirection: lastSortDirection,
      });
      projects.value.push(...(res.projects ?? []));
      cursor = res.endCursor;
      hasMore.value = res.hasNextPage;
    } finally {
      isLoadingMore.value = false;
    }
  }

  // Sort is applied server-side, so changing it re-fetches from scratch.
  async function resort(sortBy, sortDirection) {
    lastSortBy = sortBy;
    lastSortDirection = sortDirection;
    projects.value = [];
    cursor = null;
    hasMore.value = true;
    hasLoadedOnce = true;
    isLoading.value = true;
    await loadMore();
    isLoading.value = false;
  }

  // Deleting/adding a document doesn't refetch this cached list, so the
  // caller (e.g. Project.vue after a delete) patches the affected project's
  // count directly instead of it staying stale until a reload.
  function adjustDocCount(projectId, delta) {
    const p = projects.value.find((p) => p.id === projectId);
    if (p) p.totalDocuments = Math.max(0, (p.totalDocuments ?? 0) + delta);
  }

  // For when a project is deleted or left (see ProjectCardMenu.vue) — that
  // action refreshes useProjectsByTab.js's cache (Projects.vue), but this is
  // a separate cache (the side panel's "all projects" list), so it needs its
  // own update or it'd keep showing the project until a reload.
  function removeProject(projectId) {
    projects.value = projects.value.filter((p) => p.id !== projectId);
  }

  return { projects, isLoading, isLoadingMore, hasMore, ensureLoaded, loadMore, resort, adjustDocCount, removeProject };
}
